"use client";

import { createClient } from "@/lib/supabase/client";
import { cn, getReadableNumber } from "@/lib/utils";
import { Eye } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface PostViewTrackerProps {
	postId: string | null | undefined;
	className?: string;
}

export default function PostViewTracker({
	postId,
	className = "",
}: PostViewTrackerProps) {
	const [viewCount, setViewCount] = useState(0);
	const hasTrackedRef = useRef(false);

	useEffect(() => {
		if (postId && !hasTrackedRef.current) {
			// Mark as tracked immediately to prevent race conditions
			hasTrackedRef.current = true;

			const trackView = async () => {
				try {
					const { count, error } = await updatePostViews(postId);
					if (error) {
						// Reset the flag on error so it can retry
						hasTrackedRef.current = false;
					} else {
						setViewCount(count);
					}
				} catch (err) {
					// Reset the flag on error so it can retry
					hasTrackedRef.current = false;
				}
			};

			trackView();
		}
	}, [postId]);

	if (!postId || viewCount === 0) {
		return null;
	}

	return (
		<div className={cn("flex gap-1 items-center text-primary", className)}>
			<Eye className={"size-5"} />
			<span className="font-sans text-sm">
				<span className="sr-only">Total views: </span>
				{getReadableNumber(viewCount)} views
			</span>
		</div>
	);
}

// Updates the view count for a specific post - simplified version
export async function updatePostViews(
	postId: string,
): Promise<{ count: number; error: Error | null }> {
	if (!postId) {
		return { count: 0, error: new Error("Post ID is required") };
	}

	try {
		const { data, error: updateError } = await createClient().rpc(
			"increment_blog_post_views",
			{ post_id_param: postId },
		);

		if (updateError) {
			return {
				count: 0,
				error: updateError,
			};
		}

		return {
			count: data,
			error: null,
		};
	} catch (err) {
		return {
			count: 0,
			error: err instanceof Error ? err : new Error("Unknown error"),
		};
	}
}
